import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type Theme = 'dark' | 'light' | 'system';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: 'dark' | 'light';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'voicequote-theme';

function getStoredTheme(defaultTheme: Theme): Theme {
  try {
    return (localStorage.getItem(STORAGE_KEY) as Theme) || defaultTheme;
  } catch {
    return defaultTheme;
  }
}

function getSystemPrefersDark(): boolean {
  try {
    if (typeof globalThis.matchMedia !== 'function') return false;
    return globalThis.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
}

export function ThemeProvider({ children, defaultTheme = 'system' }: Readonly<ThemeProviderProps>) {
  const [theme, setTheme] = useState<Theme>(() => getStoredTheme(defaultTheme));
  const [systemDark, setSystemDark] = useState(getSystemPrefersDark);

  // Listen for OS theme changes
  useEffect(() => {
    if (typeof globalThis.matchMedia !== 'function') return;
    const mq = globalThis.matchMedia('(prefers-color-scheme: dark)');
    if (!mq?.addEventListener) return;
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Apply dark class to document
  useEffect(() => {
    const isDark = theme === 'dark' || (theme === 'system' && systemDark);
    document.documentElement?.classList?.toggle('dark', isDark);
  }, [theme, systemDark]);

  const updateTheme = useCallback(
    (newTheme: Theme) => {
      try {
        localStorage.setItem(STORAGE_KEY, newTheme);
      } catch {
        // localStorage may be unavailable
      }
      setTheme(newTheme);
    },
    [setTheme],
  );

  let resolvedTheme: 'dark' | 'light';
  if (theme === 'system') {
    resolvedTheme = systemDark ? 'dark' : 'light';
  } else {
    resolvedTheme = theme;
  }

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme: updateTheme }),
    [theme, resolvedTheme, updateTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
